import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { asBigNumber } from '@restless/ethereum'
import { asArray, asNumber, asObject, asString, cast } from '@restless/sanitizers'
import { BigNumber, constants, utils } from 'ethers'
import { isDuplicated } from 'src/utils/isDuplicated'

export class AllocationService {
  constructor(
    private client: ApolloClient<NormalizedCacheObject>,
    private startTimestampInSec: number,
    private endTimestampInSec: number,
    private allocationAmount: BigNumber,
  ) {}

  async fetchLiquidityActions() {
    const _liquidityActions = await this.client.query({ query: LIQUIDITY_ACTIONS_GQL })
    const liquidityActions = cast<LiquidityAction[]>(
      _liquidityActions.data?.liquidityActions,
      asArray(asLiquidityAction),
      'Cannot cast liquidity actions',
    )
    return liquidityActions.sort((a, b) => a.timestamp - b.timestamp)
  }

  calculateWeight(liquidityActions: LiquidityAction[]) {
    const result = liquidityActions.reduce(
      (prevValue, currentValue, index, array) => {
        const fromTimestamp = Math.max(currentValue.timestamp, this.startTimestampInSec)
        const toTimestamp = Math.min(
          array[index + 1]?.timestamp || Number.MAX_SAFE_INTEGER,
          this.endTimestampInSec,
        )
        const duration = toTimestamp - fromTimestamp
        if (currentValue.timestamp > this.endTimestampInSec) {
          return prevValue
        }
        const localAmount =
          currentValue.type === 'Deposit'
            ? prevValue.amount.add(currentValue.amount)
            : prevValue.amount.sub(currentValue.amount)
        const amount = localAmount.lt('0') ? BigNumber.from('0') : localAmount
        if (array[index + 1]?.timestamp < this.startTimestampInSec) {
          return {
            weight: prevValue.weight,
            amount,
          }
        }
        return {
          weight: prevValue.weight.add(amount.mul(duration)),
          amount,
        }
      },
      {
        amount: BigNumber.from('0'),
        weight: BigNumber.from('0'),
      },
    )
    return result.weight
  }

  getUniqueAccounts(liquidityActions: LiquidityAction[]) {
    return liquidityActions.map(({ account }) => account).filter(isDuplicated)
  }

  getSummary(liquidityActions: LiquidityAction[]) {
    const users = this.getUniqueAccounts(liquidityActions)
    const userWeights = users.map((user) => {
      const liquidityActionsForUser = liquidityActions.filter(({ account }) => account === user)
      return [user, this.calculateWeight(liquidityActionsForUser)]
    })
    const totalWeight = userWeights.reduce((prev, cur) => prev.add(cur[1]), BigNumber.from('0'))
    return userWeights.map(([user, weight]) => [
      user,
      this.allocationAmount.mul(weight).div(totalWeight),
      utils.formatEther(constants.WeiPerEther.mul(weight).div(totalWeight)),
    ])
  }
}

const LIQUIDITY_ACTIONS_GQL = gql`
  query LiquidityActions {
    liquidityActions(first: 1000) {
      account
      pool
      amount
      type
      timestamp
    }
  }
`
export type LiquidityAction = {
  account: string
  pool: string
  amount: BigNumber
  type: string
  timestamp: number
}

const asLiquidityAction = asObject<LiquidityAction>({
  account: asString,
  pool: asString,
  amount: asBigNumber,
  type: asString,
  timestamp: asNumber,
})
